import React, {
  Fragment,
  cloneElement,
  isValidElement,
  memo,
  useEffect,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import { Popover } from '@mui/material';
import { styled } from '@mui/material/styles';
import { css } from '@emotion/react';

const StyledPopover = styled(Popover)(
  (props) => css`
    .MuiPaper-root {
      margin: 0;
      font-family: inherit;
      font-weight: 400;
      font-size: 1rem;
      line-height: 1.5;
      direction: ${props.theme.direction};
      background: #fff;
      border: 1px solid #ededed;
      border-radius: 8px;
      box-shadow: 0 15px 36px rgba(0, 0, 0, 0.06),
        0 3px 8px -1px rgba(50, 50, 71, 0.05);
      ${props.paperStyles}
    }
  `
);

/**
 * A customizable popover component that automatically repositions itself when content changes.
 * Supports dynamic positioning updates through the updateTriggers prop and manual control via action ref.
 *
 * @component
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Content to display inside the popover
 * @param {Object} [props.style] - Custom styles for the popover
 * @param {React.ReactElement} [props.toggler] - Element that triggers the popover to open
 * @param {boolean} [props.open] - Whether the popover is open (for controlled usage)
 * @param {Object} [props.action] - Ref object to expose updatePosition method for manual control
 * @param {Array} [props.updateTriggers] - Array of values to watch for changes. When any value changes, the popover automatically repositions itself
 * @returns {JSX.Element} The UiPopover component
 *
 * @example
 * // Basic usage with automatic positioning updates
 * <UiPopover
 *   updateTriggers={[selectedValue, isOpen]}
 *   toggler={<button>Open Popover</button>}
 * >
 *   <div>Dynamic content that changes size</div>
 * </UiPopover>
 *
 * @example
 * // Manual control with action ref
 * const actionRef = useRef();
 *
 * <UiPopover
 *   action={actionRef}
 *   toggler={<button>Open Popover</button>}
 * >
 *   <div>Content</div>
 * </UiPopover>
 *
 * // Later, manually update position
 * actionRef.current?.updatePosition();
 */
const UiPopover = (props) => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const popoverActionRef = useRef(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'popover' : undefined;

  /**
   * Automatically updates popover position when updateTriggers change.
   * This ensures the popover repositions itself when content changes dynamically,
   * preventing it from going off-screen due to content size changes.
   *
   * @effect
   * @dependencies {boolean} props.open - Whether the popover is open
   * @dependencies {Object} props.action - Action ref for manual control
   * @dependencies {Array} props.updateTriggers - Values to watch for changes
   */
  useEffect(() => {
    if (
      (props?.open || open) &&
      !props?.action &&
      popoverActionRef.current?.updatePosition &&
      props?.updateTriggers?.length > 0
    ) {
      // Use setTimeout to ensure DOM has updated before repositioning
      setTimeout(() => {
        popoverActionRef.current?.updatePosition();
      }, 0);
    }
  }, [props?.open, props?.action, props?.updateTriggers, open]);

  return (
    <Fragment>
      {isValidElement(props.toggler) &&
        cloneElement(props.toggler, { onClick: handleClick })}
      <StyledPopover
        id={id}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        action={popoverActionRef}
        {...props}
      >
        {props.children}
      </StyledPopover>
    </Fragment>
  );
};
UiPopover.propTypes = {
  children: PropTypes.node,
  style: PropTypes.object,
  toggler: PropTypes.element,
  open: PropTypes.bool,
  action: PropTypes.object,
  updateTriggers: PropTypes.array,
};

export default memo(UiPopover);
